// ViewRenderer.jsx

import React from 'react';

// Components
import BoardView from 'views/board/BoardView';
import BoardViewCompact from 'views/board/BoardViewCompact';
import CalendarView from 'views/calendar/CalendarView';
import CalendarViewCompact from 'views/calendar/CalendarViewCompact';
import ChartView from 'views/chart/ChartView';
import ChartViewCompact from 'views/chart/ChartViewCompact';
import FeedView from 'views/feed/FeedView';
import FeedViewCompact from 'views/feed/FeedViewCompact';
import GalleryView from 'views/gallery/GalleryView';
import GalleryViewCompact from 'views/gallery/GalleryViewCompact';
import AlphaListView from 'views/alphalist/AlphaListView';
import AlphaListViewCompact from 'views/alphalist/AlphaListViewCompact';
import ListView from 'views/list/ListView';
import ListViewCompact from 'views/list/ListViewCompact';
import MapView from 'views/map/MapView';
import MapViewCompact from 'views/map/MapViewCompact';
import SchedulerView from 'views/scheduler/SchedulerView';
import TableView from 'views/table/TableView';
import TableViewCompact from 'views/table/TableViewCompact';
import TimelineView from 'views/timeline/TimelineView';

/**
 * ViewRenderer
 * 
 * Renders the appropriate view based on the view type.
 * 
 * @param {object} view - The view object containing type and other properties.
 * @param {object} typeDef - The type definition for the nodes.
 * @param {object[]} nodes - The array of nodes to display.
 * @param {string} language - The current language code.
 * 
 * @returns {ReactNode} - The rendered view component.
 */
const ViewRenderer = React.memo(({
  view,
  typeDef,
  nodes,
  parentNodeKey,
  parentNodeTypeLabel,
  language,
  addTypeKey,
  onQuickActionSelect,
  onQuickActionCreateOnly,
  selectedKeys,
  setSelectedKeys,
  tableShowCheckboxes = true,
  displayMode = 'full',
  enforceExplicitProperties = false,
  interactionMode = 'base',
  onItemClick = null,
  eventItems = undefined,
  readOnly = false,
  shareSelection = null,
}) => {
  if (!view) return null;

  const isPreviewInteraction = interactionMode === 'preview' || readOnly === true;
  const effectiveTableShowCheckboxes = isPreviewInteraction ? false : tableShowCheckboxes;
  const effectiveAddTypeKey = isPreviewInteraction ? undefined : addTypeKey;
  const effectiveOnQuickActionSelect = isPreviewInteraction ? undefined : onQuickActionSelect;
  const effectiveOnQuickActionCreateOnly = isPreviewInteraction ? undefined : onQuickActionCreateOnly;

  switch (view?.type) {
    case 'GALLERY':
      if (displayMode === 'compact') {
        return (
          <GalleryViewCompact
            enforceExplicitProperties={enforceExplicitProperties}
            view={view}
            typeDef={typeDef}
            nodes={nodes}
            language={language}
            onItemClick={onItemClick}
          />
        );
      }
      return (
        <GalleryView
          enforceExplicitProperties={enforceExplicitProperties}
          view={view}
          typeDef={typeDef}
          nodes={nodes}
          parentNodeKey={parentNodeKey}
          parentNodeTypeLabel={parentNodeTypeLabel}
          language={language}
          addTypeKey={effectiveAddTypeKey}
          onItemClick={onItemClick}
          onQuickActionSelect={effectiveOnQuickActionSelect}
          nodeDragEnabled={!isPreviewInteraction}
        />
      );
    case 'BOARD':
      if (displayMode === 'compact') {
        return (
          <BoardViewCompact
            view={view}
            typeDef={typeDef}
            nodes={nodes}
            language={language}
          />
        );
      }
      return (
        <BoardView
          view={view}
          typeDef={typeDef}
          nodes={nodes}
          parentNodeKey={parentNodeKey}
          parentNodeTypeLabel={parentNodeTypeLabel}
          language={language}
          addTypeKey={effectiveAddTypeKey}
          interactionMode={interactionMode}
          nodeDragEnabled={!isPreviewInteraction}
          onItemClick={onItemClick}
          onQuickActionCreateOnly={effectiveOnQuickActionCreateOnly}
          onQuickActionSelect={effectiveOnQuickActionSelect}
        />
      );
    case 'FEED':
      if (displayMode === 'compact') {
        return (
          <FeedViewCompact
            view={view}
            typeDef={typeDef}
            nodes={nodes}
            language={language}
            shareSelection={shareSelection}
          />
        );
      }
      return (
        <FeedView
          view={view}
          typeDef={typeDef}
          nodes={nodes}
          language={language}
          shareSelection={shareSelection}
        />
      );
    case 'CALENDAR':
      if (displayMode === 'compact') {
        return (
          <CalendarViewCompact
            view={view}
            typeDef={typeDef}
            nodes={nodes}
            eventItems={eventItems}
            language={language}
            onItemClick={onItemClick}
          />
        );
      }
      return (
        <CalendarView
          view={view}
          typeDef={typeDef}
          nodes={nodes}
          eventItems={eventItems}
          parentNodeKey={parentNodeKey}
          parentNodeTypeLabel={parentNodeTypeLabel}
          language={language}
          addTypeKey={effectiveAddTypeKey}
          interactionMode={interactionMode}
          onItemClick={onItemClick}
          onQuickActionCreateOnly={effectiveOnQuickActionCreateOnly}
          onQuickActionSelect={effectiveOnQuickActionSelect}
        />
      );
    case 'LIST':
      if (displayMode === 'compact') {
        return (
          <ListViewCompact
            enforceExplicitProperties={enforceExplicitProperties}
            view={view}
            typeDef={typeDef}
            nodes={nodes}
            language={language}
            onItemClick={onItemClick}
          />
        );
      }
      return (
        <ListView
          enforceExplicitProperties={enforceExplicitProperties}
          view={view}
          typeDef={typeDef}
          nodes={nodes}
          parentNodeKey={parentNodeKey}
          parentNodeTypeLabel={parentNodeTypeLabel}
          language={language}
          addTypeKey={effectiveAddTypeKey}
          readOnly={isPreviewInteraction}
          onItemClick={onItemClick}
          onQuickActionSelect={effectiveOnQuickActionSelect}
          nodeDragEnabled={!isPreviewInteraction}
        />
      );
    case 'ALPHA_LIST':
      if (displayMode === 'compact') {
        return (
          <AlphaListViewCompact
            enforceExplicitProperties={enforceExplicitProperties}
            view={view}
            typeDef={typeDef}
            nodes={nodes}
            language={language}
            onItemClick={onItemClick}
          />
        );
      }
      return (
        <AlphaListView
          enforceExplicitProperties={enforceExplicitProperties}
          view={view}
          typeDef={typeDef}
          nodes={nodes}
          parentNodeKey={parentNodeKey}
          parentNodeTypeLabel={parentNodeTypeLabel}
          language={language}
          readOnly={isPreviewInteraction}
          onItemClick={onItemClick}
          nodeDragEnabled={!isPreviewInteraction}
        />
      );
    case 'TIMELINE':
      if (displayMode === 'compact') {
        return (
          <TimelineView
            view={view}
            typeDef={typeDef}
            nodes={nodes}
            onItemClick={onItemClick}
          />
        );
      }
      return (
        <TimelineView
          view={view}
          typeDef={typeDef}
          nodes={nodes}
          parentNodeKey={parentNodeKey}
          parentNodeTypeLabel={parentNodeTypeLabel}
          language={language}
          onItemClick={onItemClick}
        />
      );
    case 'MAP':
      if (displayMode === 'compact') {
        return (
          <MapViewCompact
            view={view}
            typeDef={typeDef}
            nodes={nodes}
            language={language}
            onItemClick={onItemClick}
          />
        );
      }
      return (
        <MapView
          view={view}
          typeDef={typeDef}
          nodes={nodes}
          language={language}
          onItemClick={onItemClick}
        />
      );
    case 'SCHEDULER':
      return (
        <SchedulerView
          view={view}
          typeDef={typeDef}
          nodes={nodes}
          displayMode={displayMode}
          onQuickActionCreateOnly={effectiveOnQuickActionCreateOnly}
          onQuickActionSelect={effectiveOnQuickActionSelect}
        />
      );
    case 'CHART':
      if (displayMode === 'compact') {
        return (
          <ChartViewCompact
            view={view}
            typeDef={typeDef}
            nodes={nodes}
          />
        );
      }
      return (
        <ChartView
          view={view}
          typeDef={typeDef}
          nodes={nodes}
        />
      );
    case 'TABLE':
      if (displayMode === 'compact') {
        return (
          <TableViewCompact
            enforceExplicitProperties={enforceExplicitProperties}
            view={view}
            typeDef={typeDef}
            nodes={nodes}
            language={language}
            onRowClick={onItemClick}
          />
        );
      }
      return (
        <TableView
          view={view}
          typeDef={typeDef}
          nodes={nodes}
          showCheckboxes={effectiveTableShowCheckboxes}
          addTypeKey={effectiveAddTypeKey}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          readOnly={isPreviewInteraction}
          nodeDragEnabled={!isPreviewInteraction}
          onQuickActionCreateOnly={effectiveOnQuickActionCreateOnly}
          onQuickActionSelect={effectiveOnQuickActionSelect}
        />
      );
    default:

      return (
        <TableView
          view={view}
          typeDef={typeDef}
          nodes={nodes}
          showCheckboxes={effectiveTableShowCheckboxes}
          addTypeKey={effectiveAddTypeKey}
          nodeDragEnabled={!isPreviewInteraction}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          readOnly={isPreviewInteraction}
          onQuickActionCreateOnly={effectiveOnQuickActionCreateOnly}
          onQuickActionSelect={effectiveOnQuickActionSelect}
        />
      );
  }
});

// Dev Tools
ViewRenderer.displayName = 'ViewRenderer';

export default ViewRenderer;
